import { globalRef, type Timestamp } from '@amazon/vinyl-util'

/** DevTools Performance-panel top group shared by every amu app timing. */
export const APP_TIMINGS_GROUP = 'Amu app timings'

/** DevTools track (lane) name for one track's load journey, named by the song. */
export function trackTimingLane(track: string): string {
    return `Track load: ${track}`
}

/**
 * DevTools lane for raw per-operation GraphQL spans (labeled `gqlFetch_<op>`),
 * so every query is a named contributor instead of collapsing into the shared
 * group's default lane. A track-scoped fetch overrides this with its own
 * {@link trackTimingLane}.
 */
export const GQL_QUERIES_LANE = 'GraphQL queries'

/**
 * DevTools lane for the auth / request-init work that gates a gql fetch —
 * `getAuthToken`, `fireflyApiKey`, and the per-op `<label> auth` bar — so the
 * auth wait is a named contributor instead of hiding inside the fetch bar. On
 * AuthPortal this surfaces the `/config.json` boot wait that gates every call.
 */
export const GQL_AUTH_LANE = 'GraphQL auth'

/**
 * One `console.timeStamp` bar in the DevTools Performance panel. `start`/`end`
 * are unix-ms {@link Timestamp}s — the same clock vinyl's `loadSpan` uses — so
 * the journey span and its nested load spans share one timeline; the real impl
 * shifts them onto the panel's `performance.now()` scale.
 */
export interface TimeStampOptions {
    readonly label: string
    readonly start?: Timestamp
    readonly end?: Timestamp
    readonly trackName?: string
    readonly trackGroup?: string
    readonly color?: string
}

/**
 * The app-wide performance-instrumentation seam. Draws DevTools Performance-panel
 * bars via `console.timeStamp` only — no native User Timings. `now()` returns a
 * unix-ms {@link Timestamp}. A single seam: `performanceRef` builds the real
 * console-drawing impl in one place — always on, so the same spans feed prod
 * metrics — without touching any call site.
 */
export interface PerformanceApi {
    now(): Timestamp
    timeStamp(options: TimeStampOptions): void
}

// The extended `console.timeStamp(label, start, end, trackName, trackGroup,
// color)` form is a DevTools extension the lib types omit; older hosts ignore
// the extra args and just log. Numeric start/end are `performance.now()`-scale.
// https://developer.chrome.com/docs/devtools/performance/extension
type DevToolsTimeStamp = (
    label: string,
    start?: number,
    end?: number,
    trackName?: string,
    trackGroup?: string,
    color?: string
) => void

/**
 * Draws `console.timeStamp` bars in the Performance panel. Unix-ms
 * {@link Timestamp}s are shifted by `timeOrigin` onto the panel's
 * `performance.now()` scale; a throw (or a missing `console.timeStamp` on an
 * older host) must never break the instrumented caller.
 */
export function createConsoleTimeStampApi(
    timeOrigin: number = typeof performance !== 'undefined' && performance.timeOrigin
        ? performance.timeOrigin
        : 0
): PerformanceApi {
    const toPanel = (t: Timestamp | undefined): number | undefined =>
        t == null ? undefined : t - timeOrigin
    return {
        now: () => Date.now(),
        timeStamp: ({ label, start, end, trackName, trackGroup, color }) => {
            try {
                if (typeof console === 'undefined') return
                const draw = console.timeStamp as unknown as DevToolsTimeStamp | undefined
                draw?.call(
                    console,
                    label,
                    toPanel(start),
                    toPanel(end),
                    trackName,
                    trackGroup,
                    color
                )
            } catch {
                // Instrumentation must never throw into an instrumented caller.
            }
        },
    }
}

// Always the bar-drawing impl — the spans feed prod metrics too, not just
// `?debug` DevTools. `console.timeStamp` (extended form) is a harmless no-op
// where unsupported, so this stays cheap on every host.
export const performanceRef = globalRef<PerformanceApi>(() => createConsoleTimeStampApi())

/** The app-wide performance instrumentation seam. */
export function getPerformance(): PerformanceApi {
    return performanceRef.value
}
